/* eslint-disable react/prop-types */
import React from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import { CustomBadge } from './CustomBadge'
import { Button, Col, Row, Space } from 'antd'
import { QuestionCircleOutlined } from '@ant-design/icons'
import { withTextEffect } from './TextEffects/TextEffectBase'

/**
 * GeneralTourTitle renders a header with optional title and tour button,
 * laid out in an AntD Row/Col structure. Children render to the right.
 *
 * @component
 * @param {Object} props
 * @param {Object} [props.rowProps] – Props forwarded to the Row container.
 * @param {Object} [props.titleProps] – { text, h, style, ... } passed to GeneralTitle.
 * @param {Object} [props.buttonProps] – { onClick, icon, text, ... } passed to GeneralTourButton.
 * @param {React.ReactNode} [props.children] – Additional content rendered alongside the title.
 * @returns {JSX.Element}
 */
export const GeneralTourTitle = ({
	rowProps,
	titleProps,
	buttonProps,
	children,
	...props
}) => {
	const { align = 'center', ...restProps } = props
	const { style: titleStyle, ...restTitleProps } = titleProps || {}
	return (
		<Row
			gutter={[16, 16]}
			align="middle"
			{...rowProps}
			className="we-general-tour-title"
		>
			<Col>
				<Space style={{ width: '100%' }} align={align} {...restProps}>
					{titleProps?.text && (
						<GeneralTitle {...restTitleProps} style={titleStyle} />
					)}
					{buttonProps?.onClick && (
						<GeneralTourButton {...buttonProps} />
					)}
				</Space>
			</Col>
			<Col>{children}</Col>
		</Row>
	)
}
GeneralTourTitle.propTypes = {
	rowProps: PropTypes.object,
	titleProps: PropTypes.object,
	buttonProps: PropTypes.object,
	align: PropTypes.string,
	children: PropTypes.node
}

export const GeneralParagraph = ({
	h = 'p',
	style = {},
	className = '',
	children,
	textEffect,
	textEffectProps,
	...props
}) => {
	const H = h
	const ParagraphBase = withTextEffect(H)

	return (
		<ParagraphBase
			textEffect={textEffect}
			textEffectProps={textEffectProps}
			style={{ ...style }}
			className={`${className} we-general-paragraph`}
			{...props}
		>
			{children}
		</ParagraphBase>
	)
}

GeneralParagraph.propTypes = {
	h: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
	style: PropTypes.object,
	className: PropTypes.string,
	children: PropTypes.node,
	textEffect: PropTypes.string,
	textEffectProps: PropTypes.object
}

// with text effects support
export const GeneralButton = ({
	h = Button,
	style = {},
	className = '',
	children,
	...props
}) => {
	const H = h

	// dynamically enhance with effect only when needed
	const ButtonBase = props.textEffect ? withTextEffect(H) : H

	return (
		<ButtonBase
			textEffect={props.textEffect}
			textEffectProps={props.textEffectProps}
			style={{ ...style }}
			className={`${className} we-general-button`}
			{...props}
		>
			{children}
		</ButtonBase>
	)
}
GeneralButton.propTypes = {
	h: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
	style: PropTypes.object,
	className: PropTypes.string,
	children: PropTypes.node,
	textEffect: PropTypes.string,
	textEffectProps: PropTypes.object
}

/**
 * GeneralTourButton renders a styled Button for triggering tours or help.
 *
 * @component
 * @param {Object} props
 * @param {Function} props.onClick – Click handler.
 * @param {string} [props.type='link'] – AntD Button type.
 * @param {React.ReactNode} [props.icon=<QuestionCircleOutlined/>] – Icon displayed.
 * @param {string} [props.text] – Button label.
 * @param {Object} [props.style] – Inline styles.
 * @returns {JSX.Element}
 */
export const GeneralTourButton = ({ onClick, style = {}, ...props }) => {
	return (
		<Button
			className="no-print we-general-tour-button"
			type={props?.type || 'link'}
			icon={props?.icon || <QuestionCircleOutlined />}
			onClick={onClick}
			style={{ ...style }}
			{...props}
		>
			{props?.text}
		</Button>
	)
}
GeneralTourButton.propTypes = {
	onClick: PropTypes.func,
	type: PropTypes.string,
	icon: PropTypes.node,
	text: PropTypes.string,
	style: PropTypes.object
}

/**
 * GeneralTitle renders a semantic heading (h1–h6 or custom) with text and children.
 *
 * @component
 * @param {Object} props
 * @param {string} props.text – The heading text.
 * @param {string|React.ElementType} [props.h='h1'] – Heading level or custom component.
 * @param {string} [props.className] – CSS classes.
 * @param {Object} [props.style] – Inline styles.
 * @param {React.ReactNode} [props.children] – Additional inline content.
 * @returns {JSX.Element}
 */
export const GeneralTitle = ({
	text,
	h = 'h1',
	className,
	style = {},
	children,
	textEffect,
	textEffectProps,
	...props
}) => {
	const H = h
	const TitleBase = withTextEffect(H)

	return (
		<TitleBase
			textEffect={textEffect}
			textEffectProps={textEffectProps}
			className={`${className ?? ''} we-general-title`}
			style={style}
			{...props}
		>
			{text}
			{children}
		</TitleBase>
	)
}

GeneralTitle.propTypes = {
	text: PropTypes.string,
	h: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
	className: PropTypes.string,
	style: PropTypes.object,
	children: PropTypes.node,
	textEffect: PropTypes.string,
	textEffectProps: PropTypes.object
}

/**
 * GeneralBcrumb renders a breadcrumb navigation as Links.
 *
 * @component
 * @param {Object} props
 * @param {Array<{to: string, text: string}>} props.itms – Array of breadcrumb items.
 * @returns {JSX.Element}
 */
export const GeneralBcrumb = ({ itms }) => {
	return (
		<div className="bcrumb h-gap-medium h-flex we-general-bcrumb">
			{itms.map((itm) => {
				return (
					<Link
						key={`${itm.to}`}
						className="bcrumb-item"
						to={itm.to}
						relative="path"
					>
						{itm.text}
					</Link>
				)
			})}
		</div>
	)
}
GeneralBcrumb.propTypes = {
	itms: PropTypes.arrayOf(
		PropTypes.shape({
			to: PropTypes.string,
			text: PropTypes.string
		})
	)
}

/**
 * GeneralTabGroup renders a horizontal tab list. Active tabs are highlighted,
 * optional badges display counts, and custom components may be used per item.
 *
 * @component
 * @param {Object} props
 * @param {Array<{
 *   to: string,
 *   text: string,
 *   active?: boolean,
 *   count?: number,
 *   disabled?: boolean,
 *   custom?: React.ComponentType
 * }>} props.itms – Tab definitions.
 * @returns {JSX.Element}
 */
export const GeneralTabGroup = ({ itms }) => {
	return (
		<div className="tabgroup-style-3 we-general-tab-group">
			{itms.map((itm) => {
				if (itm.disabled) return null
				if (itm.custom) {
					const CustomComponent = itm.custom
					return <CustomComponent key={itm.key} />
				}
				const linkElement = (
					<Link
						key={`${itm.to}`}
						to={itm.to}
						relative="path"
						className={`tab ${itm?.active ? 'active' : ''}`}
					>
						{itm.text}
					</Link>
				)

				return itm.count ? (
					<CustomBadge key={`${itm.to}`} count={itm.count}>
						{linkElement}
					</CustomBadge>
				) : (
					linkElement
				)
			})}
		</div>
	)
}
GeneralTabGroup.propTypes = {
	itms: PropTypes.arrayOf(
		PropTypes.shape({
			to: PropTypes.string,
			text: PropTypes.string,
			active: PropTypes.bool,
			count: PropTypes.number
		})
	)
}

/**
 * GeneralContentWrapper provides a vertical Space container
 * that wraps arbitrary content with full width.
 *
 * @component
 * @param {Object} props
 * @param {React.ReactNode} props.children – Elements to render inside the wrapper.
 * @returns {JSX.Element}
 */
export const GeneralContentWrapper = ({ children }) => {
	return (
		<Space style={{ width: '100%' }} direction="vertical">
			{children}
		</Space>
	)
}
GeneralContentWrapper.propTypes = {
	children: PropTypes.node
}
